
/* eslint-disable  @typescript-eslint/no-explicit-any */

import { addDays } from "date-fns";
import Highcharts, { Options, SeriesAbandsOptions, SeriesOptionsType } from "highcharts";
import { AlarmNotificationEndingReasonEnum } from "../../common/enums/AlarmNotificationEndingReasonEnum";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { DeviceTypesEnum } from "../../common/enums/DeviceTypesEnum";
import { DateRangeType, DurationTimeframeTypeEnum } from "../../common/enums/DRCXEnums";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { MemoType } from "../../common/enums/MemoType";
import { SetPointEventTypeEnum } from "../../common/enums/SetPointEventTypeEnum";
import { IDRCXConfigurationTimeframe } from "../../common/models/drcx/DRCXConfigurationTimeframe";
import { IDrcxEffectiveTimeModel } from "../../common/models/drcx/DRCXEffectiveTimeModel";
import { IDRCXTimeAndDuration } from "../../common/models/drcx/DRCXTimeAndDuration";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IListTextTagMapping_V2 } from "../../common/models/ILookupList";
import { CheckListItemType } from "../../components/check-list/CheckList";

import { ITrendConfigurationDTO, TrendConfigurationModel, TrendingTimeAndDuration } from "../trending-configuration-data-service/Models";

export interface ITrendingEffectiveTime extends IDrcxEffectiveTimeModel {
    minChartTime: number;
    maxChartTime: number;
}

export interface ITrendingDisplayOptionsDTO {
    comparisonTimeframe?: IDRCXConfigurationTimeframe;
    displayAlarms: boolean | undefined;
    displayDataQualityEvents: boolean | undefined;
    displayDiagnosticEvents: boolean | undefined;
    displayManualEvents: boolean | undefined;
    displayLegend: boolean | undefined;
    displayMemos: boolean | undefined;
    displaySectionsStacked: boolean | undefined;
    displayTabularData: boolean | undefined;
    displaySettingsChanges: boolean | undefined;
    displaySimulationDataQualityEvents: boolean | undefined;
    displaySimulationDiagnosticEvents: boolean | undefined;
    hasComparisonTimeframe: boolean | undefined;
}

export interface ITrendingDisplayOptions {
    comparisonTimeAndDuration: IDRCXTimeAndDuration | undefined;
    displayAlarms: boolean | undefined;
    displayDataQualityEvents: boolean | undefined;
    displayDiagnosticEvents: boolean | undefined;
    displayManualEvents: boolean | undefined;
    displayLegend: boolean | undefined;
    displayMemos: boolean | undefined;
    displaySectionsStacked: boolean | undefined;
    displayTabularData: boolean | undefined;
    displaySettingsChanges: boolean | undefined;
    displaySimulationDataQualityEvents: boolean | undefined;
    displaySimulationDiagnosticEvents: boolean | undefined;
    hasComparisonTimeframe: boolean | undefined;

    fromDTO?(dto?: ITrendingDisplayOptionsDTO): ITrendingDisplayOptions;
    toDTO?(): ITrendingDisplayOptionsDTO;
}

export class TrendingDisplayOptions implements ITrendingDisplayOptions {
    public comparisonTimeAndDuration: IDRCXTimeAndDuration | undefined = new TrendingTimeAndDuration();
    public displayAlarms: boolean | undefined = false;
    public displayDataQualityEvents: boolean | undefined = false;
    public displayDiagnosticEvents: boolean | undefined = false;
    public displayManualEvents: boolean | undefined = false;
    public displayLegend: boolean | undefined = false;
    public displayMemos: boolean | undefined = false;
    public displaySectionsStacked: boolean | undefined = false;
    public displayTabularData: boolean | undefined = false;
    public displaySettingsChanges: boolean | undefined = false;
    public displaySimulationDataQualityEvents: boolean | undefined = false;
    public displaySimulationDiagnosticEvents: boolean | undefined = false;
    public hasComparisonTimeframe: boolean | undefined = false;

    public constructor(dto?: ITrendingDisplayOptions) {

        if (dto) {
            //User Story 53044: Research - 2025 Q2 - Review GAS report
            this.comparisonTimeAndDuration = dto.comparisonTimeAndDuration !== undefined && dto.comparisonTimeAndDuration !== null
                ? dto.comparisonTimeAndDuration : undefined;

            this.displayAlarms = dto.displayAlarms !== undefined && dto.displayAlarms !== null
                ? dto.displayAlarms : undefined;

            this.displayDataQualityEvents = dto.displayDataQualityEvents !== undefined && dto.displayDataQualityEvents !== null
                ? dto.displayDataQualityEvents : undefined;

            this.displayDiagnosticEvents = dto.displayDiagnosticEvents !== undefined && dto.displayDiagnosticEvents !== null
                ? dto.displayDiagnosticEvents : undefined;

            this.displayManualEvents = dto.displayManualEvents !== undefined && dto.displayManualEvents !== null
                ? dto.displayManualEvents : undefined;

            this.displayLegend = dto.displayLegend !== undefined && dto.displayLegend !== null
                ? dto.displayLegend : undefined;

            this.displayMemos = dto.displayMemos !== undefined && dto.displayMemos !== null
                ? dto.displayMemos : undefined;

            this.displaySectionsStacked = dto.displaySectionsStacked !== undefined && dto.displaySectionsStacked !== null
                ? dto.displaySectionsStacked : undefined;

            this.displaySettingsChanges = dto.displaySettingsChanges !== undefined && dto.displaySettingsChanges !== null
                ? dto.displaySettingsChanges : undefined;

            this.displayTabularData = dto.displayTabularData !== undefined && dto.displayTabularData !== null
                ? dto.displayTabularData : undefined;

            this.displaySimulationDataQualityEvents = dto.displaySimulationDataQualityEvents !== undefined &&
                dto.displaySimulationDataQualityEvents !== null ? dto.displaySimulationDataQualityEvents : undefined;

            this.displaySimulationDiagnosticEvents = dto.displaySimulationDiagnosticEvents !== undefined &&
                dto.displaySimulationDiagnosticEvents !== null ? dto.displaySimulationDiagnosticEvents : undefined;

            this.hasComparisonTimeframe = dto.hasComparisonTimeframe !== undefined && dto.hasComparisonTimeframe !== null
                ? dto.hasComparisonTimeframe : undefined;
        }
    }

    public static fromDTO(dto?: ITrendingDisplayOptionsDTO): ITrendingDisplayOptions | undefined {

        let displayOptions: ITrendingDisplayOptions | undefined = undefined;

        if (dto) {

            displayOptions = new TrendingDisplayOptions({

                comparisonTimeAndDuration: dto.comparisonTimeframe ?
                    TrendConfigurationModel.getComparisonTimeAndDuration(dto.comparisonTimeframe) : undefined,
                displayAlarms: dto.displayAlarms,
                displayDataQualityEvents: dto.displayDataQualityEvents,
                displayDiagnosticEvents: dto.displayDiagnosticEvents,
                displayManualEvents: dto.displayManualEvents,
                displayLegend: dto.displayLegend,
                displayMemos: dto.displayMemos,
                displaySectionsStacked: dto.displaySectionsStacked,
                displayTabularData: dto.displayTabularData,
                displaySettingsChanges: dto.displaySettingsChanges,
                displaySimulationDataQualityEvents: dto.displaySimulationDataQualityEvents,
                displaySimulationDiagnosticEvents: dto.displaySimulationDiagnosticEvents,
                hasComparisonTimeframe: dto.hasComparisonTimeframe
            });

            // The UI expects positive numbers but the backend expects negative numbers so we must
            //  convert here.
            if (displayOptions.comparisonTimeAndDuration?.multipleStartOffset !== undefined) {
                displayOptions.comparisonTimeAndDuration.multipleStartOffset =
                    Math.abs(displayOptions.comparisonTimeAndDuration?.multipleStartOffset);
            }
        }

        return displayOptions;
    }

    public toDTO(): ITrendingDisplayOptionsDTO {
        return this._toDTO({} as any);
    }

    protected _toDTO(dto: ITrendingDisplayOptionsDTO): ITrendingDisplayOptionsDTO {

        dto.comparisonTimeframe = this.hasComparisonTimeframe && this.comparisonTimeAndDuration ?
            TrendConfigurationModel.getTimeframeDTO(this.comparisonTimeAndDuration)
            : undefined;

        if (dto.comparisonTimeframe?.calculatedStartDateOffset?.duration !== undefined) {

            // The UI expects positive numbers but the backend expects negative numbers so we must
            //  convert here.
            dto.comparisonTimeframe.calculatedStartDateOffset.duration =
                -dto.comparisonTimeframe?.calculatedStartDateOffset?.duration;
        }

        dto.displayAlarms = this.displayAlarms;
        dto.displayDataQualityEvents = this.displayDataQualityEvents;
        dto.displayDiagnosticEvents = this.displayDiagnosticEvents;
        dto.displayManualEvents = this.displayManualEvents;
        dto.displayLegend = this.displayLegend;
        dto.displayMemos = this.displayMemos;
        dto.displaySectionsStacked = this.displaySectionsStacked;
        dto.displayTabularData = this.displayTabularData;
        dto.displaySettingsChanges = this.displaySettingsChanges;
        dto.displaySimulationDataQualityEvents = this.displaySimulationDataQualityEvents;
        dto.displaySimulationDiagnosticEvents = this.displaySimulationDiagnosticEvents;
        dto.hasComparisonTimeframe = this.hasComparisonTimeframe;

        return dto;
    }
}

export interface ITrendingDataRequestModelDTO {
    configurationID?: number;
    configuration?: ITrendConfigurationDTO;
    disableDeferredData: boolean;
    displayOptions?: ITrendingDisplayOptionsDTO | undefined;
    endTime?: Date;
    startTime?: Date;
    windowHeight?: number;
    windowWidth?: number;
}

export class TrendingDataRequestModel implements ITrendingDataRequestModel {
    configurationID?: number;
    configuration?: TrendConfigurationModel;
    disableDeferredData: boolean = false;
    displayOptions?: ITrendingDisplayOptions;
    endTime?: Date;
    startTime?: Date;
    windowHeight?: number;
    windowWidth?: number;

    public constructor(dto?: ITrendingDataRequestModel) {

        if (dto) {
            //User Story 53044: Research - 2025 Q2 - Review GAS report
            this.configurationID = dto.configurationID !== undefined && dto.configurationID !== null
                ? dto.configurationID : undefined;

            this.configuration = dto.configuration !== undefined && dto.configuration !== null
                ? dto.configuration : undefined;

            this.disableDeferredData = dto.disableDeferredData !== undefined && dto.disableDeferredData !== null
                ? dto.disableDeferredData : false;

            this.displayOptions = dto.displayOptions !== undefined && dto.displayOptions !== null
                ? new TrendingDisplayOptions(dto.displayOptions) : undefined;

            this.endTime = dto.endTime !== undefined && dto.endTime !== null
                ? dto.endTime : undefined;

            this.startTime = dto.startTime !== undefined && dto.startTime !== null
                ? dto.startTime : undefined;

            this.windowHeight = dto.windowHeight !== undefined && dto.windowHeight !== null
                ? dto.windowHeight : undefined;

            this.windowWidth = dto.windowWidth !== undefined && dto.windowWidth !== null
                ? dto.windowWidth : undefined;
        }
    }

    public toDTO(): ITrendingDataRequestModelDTO {
        return this._toDTO({} as any);
    }

    protected _toDTO(dto: ITrendingDataRequestModelDTO): ITrendingDataRequestModelDTO {

        dto.configurationID = this.configurationID;
        dto.configuration = this.configuration?.toDTO();
        dto.disableDeferredData = this.disableDeferredData;

        dto.displayOptions = this.displayOptions && this.displayOptions?.toDTO ?
            this.displayOptions.toDTO()
            : undefined;

        dto.endTime = this.endTime;
        dto.startTime = this.startTime;
        dto.windowHeight = this.windowHeight;
        dto.windowWidth = this.windowWidth;

        return dto;
    }
}

export interface ITrendingDataRequestModel {
    configurationID?: number;
    configuration?: TrendConfigurationModel;
    disableDeferredData: boolean;
    displayOptions?: ITrendingDisplayOptions;
    endTime?: Date;
    startTime?: Date;
    windowHeight?: number;
    windowWidth?: number;
    toDTO?(): ITrendingDataRequestModelDTO;
}

export interface ITrendingTabularDataRequestModel {
    configurationID?: number;
    endTime?: Date;
    startTime?: Date;
}

export interface ITrendDataViewModelDTO {

    chartOptions: Highcharts.Options;
    effectiveTime: ITrendingEffectiveTime;
    eventCount: number;
    simulationSessionID: string;
}

export interface ITrendDataResultsModelDTO {

    configID: number;
    deviceCount: number;
    displayOptions: ITrendingDisplayOptionsDTO;
    effectiveTime: ITrendingEffectiveTime;
    eLynxObject: IeLynxObject;
    estimatedDataValues: number;
    exEstimatedDataValues: number;
    isDataDeferred: boolean;
    isMaxDataValuesExceeded: boolean;
    isMaxRowsExceeded: boolean;
    ltEstimatedDataValues: number;
    listTextTagMapping: IListTextTagMapping_V2 | undefined;
    maxDataValues: number;
    maxRows: number;
    notificationID: string;
    refreshEnabled: boolean | undefined;
    refreshSeconds: number;
    sectionCount: number;
    sectionNames: Array<string>;
    trendViewModels: Array<ITrendDataViewModelDTO>;
    wellCount: number;
}

export interface ITrendExportResultsModelDTO {

    estimatedDataValues: number;
    exEstimatedDataValues: number;
    ltEstimatedDataValues: number;
    estimatedRows: number;
    exEstimatedRows: number;
    ltEstimatedRows: number;
    isMaxDataValuesExceeded: boolean;
    isMaxRowsExceeded: boolean;
    maxDataValues: number;
    maxRows: number;
}

export interface ITrendDataViewModel {

    chartOptions: Highcharts.Options | undefined;
    effectiveTime: ITrendingEffectiveTime | undefined;
    eventCount: number | undefined;
    simulationSessionID: string | undefined;
    fromDTO?(dto?: ITrendDataViewModelDTO): ITrendDataViewModel;
}

export interface ITrendDataResultsModel {

    configID: number | undefined;
    deviceCount: number;
    displayOptions: ITrendingDisplayOptions | undefined;
    effectiveTime: ITrendingEffectiveTime | undefined;
    eLynxObject: IeLynxObject | undefined;
    estimatedDataValues: number | undefined;
    isDataDeferred: boolean | undefined;
    isMaxDataValuesExceeded: boolean | undefined;
    isMaxRowsExceeded: boolean | undefined;
    listTextTagMapping: IListTextTagMapping_V2 | undefined;
    maxDataValues: number | undefined;
    maxRows: number | undefined;
    notificationID: string | undefined;
    refreshEnabled: boolean | undefined;
    refreshSeconds: number | undefined;
    sectionCount: number | undefined;
    sectionNames: Array<string> | undefined;
    trendViewModels: Array<ITrendDataViewModel> | undefined;
    wellCount: number;
}

export class TrendDataViewModel implements ITrendDataViewModel {

    chartOptions: Highcharts.Options | undefined;
    effectiveTime: ITrendingEffectiveTime | undefined;
    eventCount: number | undefined;
    simulationSessionID: string | undefined;

    public constructor(dto?: ITrendDataViewModel) {

        if (dto) {

            this.chartOptions = dto.chartOptions !== undefined
                ? dto.chartOptions : undefined;

            this.effectiveTime = dto.effectiveTime !== undefined
                ? dto.effectiveTime : undefined;

            this.eventCount = dto.eventCount !== undefined && dto.eventCount !== null
                ? dto.eventCount : undefined;

            this.simulationSessionID = dto.simulationSessionID !== undefined && dto.simulationSessionID !== null
                ? dto.simulationSessionID : undefined;
        }
    }

    public static fromDTO(dto?: ITrendDataViewModelDTO): ITrendDataViewModel | undefined {

        let trendViewModel: ITrendDataViewModel | undefined = undefined;

        if (dto) {

            trendViewModel = new TrendDataViewModel({
                chartOptions: dto.chartOptions,
                effectiveTime: dto.effectiveTime,
                eventCount: dto.eventCount,
                simulationSessionID: dto.simulationSessionID
            });
        }

        return trendViewModel;
    }
}

export class TrendDataResultsModel implements ITrendDataResultsModel {

    configID: number | undefined;
    deviceCount: number = 0;
    displayOptions: ITrendingDisplayOptions | undefined;
    effectiveTime: ITrendingEffectiveTime | undefined;
    eLynxObject: IeLynxObject | undefined;
    estimatedDataValues: number | undefined;
    isDataDeferred: boolean | undefined;
    isMaxDataValuesExceeded: boolean | undefined = false;
    isMaxRowsExceeded: boolean | undefined = false;
    listTextTagMapping: IListTextTagMapping_V2 | undefined;
    maxDataValues: number | undefined;
    maxRows: number | undefined;
    notificationID: string | undefined;
    refreshEnabled: boolean | undefined;
    refreshSeconds: number | undefined;
    sectionCount: number | undefined;
    sectionNames: Array<string> | undefined;
    trendViewModels: Array<ITrendDataViewModel> | undefined;
    wellCount: number = 0;

    public constructor(dto?: ITrendDataResultsModel) {

        if (dto) {

            this.configID = dto.configID !== undefined && dto.configID !== null
                ? dto.configID : undefined;

            this.deviceCount = dto.deviceCount;

            this.displayOptions = dto.displayOptions !== undefined && dto.displayOptions !== null
                ? dto.displayOptions : undefined;

            this.effectiveTime = dto.effectiveTime !== undefined
                ? dto.effectiveTime : undefined;

            this.eLynxObject = dto.eLynxObject !== undefined && dto.eLynxObject !== null
                ? dto.eLynxObject : undefined;

            this.estimatedDataValues = dto.estimatedDataValues !== undefined && dto.estimatedDataValues !== null
                ? dto.estimatedDataValues : undefined;

            this.isDataDeferred = dto.isDataDeferred !== undefined && dto.isDataDeferred !== null
                ? dto.isDataDeferred : false;

            this.isMaxDataValuesExceeded = dto.isMaxDataValuesExceeded !== undefined && dto.isMaxDataValuesExceeded !== null
                ? dto.isMaxDataValuesExceeded : false;

            this.isMaxRowsExceeded = dto.isMaxRowsExceeded !== undefined && dto.isMaxRowsExceeded !== null
                ? dto.isMaxRowsExceeded : false;

            this.listTextTagMapping = dto.listTextTagMapping !== undefined && dto.listTextTagMapping !== null
                ? dto.listTextTagMapping : undefined;

            this.maxDataValues = dto.maxDataValues !== undefined && dto.maxDataValues !== null
                ? dto.maxDataValues : undefined;

            this.maxRows = dto.maxRows !== undefined && dto.maxRows !== null
                ? dto.maxRows : undefined;

            this.notificationID = dto.notificationID !== undefined && dto.notificationID !== null
                ? dto.notificationID : undefined;

            this.refreshEnabled = dto.refreshEnabled !== undefined ? dto.refreshEnabled : true;

            this.refreshSeconds = dto.refreshSeconds !== undefined && dto.refreshSeconds !== null
                ? dto.refreshSeconds : undefined;

            this.sectionCount = dto.sectionCount !== undefined && dto.sectionCount !== null
                ? dto.sectionCount : undefined;

            this.sectionNames = dto.sectionNames !== undefined && dto.sectionNames !== null
                ? dto.sectionNames : undefined;

            this.trendViewModels = dto.trendViewModels !== undefined && dto.trendViewModels !== null
                ? dto.trendViewModels : undefined;

            this.wellCount = dto.wellCount;
        }
    }

    public static getDefaultComparisonDurationType = () => DurationTimeframeTypeEnum.MultipleTimeFrames;
    public static getDefaultComparisonRangeType = () => DateRangeType.Day;
    public static getDefaultComparisonStartOffset = () => -6;
    public static getDefaultComparisonStartDate = () => addDays(new Date(), -7);

    public static fromDTO(dto?: ITrendDataResultsModelDTO): ITrendDataResultsModel | undefined {

        let tdModel: ITrendDataResultsModel | undefined = undefined;

        if (dto) {

            tdModel = new TrendDataResultsModel({
                configID: dto.configID,
                deviceCount: dto.deviceCount,
                displayOptions: TrendingDisplayOptions.fromDTO(dto.displayOptions),
                effectiveTime: dto.effectiveTime,
                eLynxObject: dto.eLynxObject,
                estimatedDataValues: dto.estimatedDataValues,
                isDataDeferred: dto.isDataDeferred,
                isMaxDataValuesExceeded: dto.isMaxDataValuesExceeded,
                isMaxRowsExceeded: dto.isMaxRowsExceeded,
                listTextTagMapping: dto.listTextTagMapping,
                maxDataValues: dto.maxDataValues,
                maxRows: dto.maxRows,
                notificationID: dto.notificationID,
                refreshEnabled: dto.refreshEnabled !== undefined ? dto.refreshEnabled : true,
                refreshSeconds: dto.refreshSeconds,
                sectionCount: dto.sectionCount,
                sectionNames: dto.sectionNames,
                trendViewModels: dto.trendViewModels,
                wellCount: dto.wellCount
            });

            // We need some defaults for the override UI.
            if (tdModel.displayOptions && tdModel.displayOptions?.comparisonTimeAndDuration === undefined) {

                tdModel.displayOptions.comparisonTimeAndDuration = new TrendingTimeAndDuration();
            }

            if (tdModel.displayOptions?.comparisonTimeAndDuration) {

                tdModel.displayOptions.comparisonTimeAndDuration.durationType =
                    tdModel.displayOptions.comparisonTimeAndDuration.durationType !== undefined
                        ? tdModel.displayOptions.comparisonTimeAndDuration.durationType
                        : TrendDataResultsModel.getDefaultComparisonDurationType();

                tdModel.displayOptions.comparisonTimeAndDuration.multipleStartRangeType =
                    tdModel.displayOptions.comparisonTimeAndDuration.multipleStartRangeType !== undefined
                        ? tdModel.displayOptions.comparisonTimeAndDuration.multipleStartRangeType
                        : TrendDataResultsModel.getDefaultComparisonRangeType();

                tdModel.displayOptions.comparisonTimeAndDuration.multipleStartOffset =
                    tdModel.displayOptions.comparisonTimeAndDuration.multipleStartOffset !== undefined
                        ? tdModel.displayOptions.comparisonTimeAndDuration.multipleStartOffset
                        : Math.abs(TrendDataResultsModel.getDefaultComparisonStartOffset());

                tdModel.displayOptions.comparisonTimeAndDuration.fixedStartDate =
                    tdModel.displayOptions.comparisonTimeAndDuration.fixedStartDate !== undefined
                        ? tdModel.displayOptions.comparisonTimeAndDuration.fixedStartDate
                        : TrendDataResultsModel.getDefaultComparisonStartDate();
            }
        }

        return tdModel;
    }
}

export type QuickGraphObject = {
    id: number;
    objectType: eLynxObjectTypeEnum;
    deviceTypeEnum?: DeviceTypesEnum;
    displayText?: string;
}

export type QuickGraphTag = {
    displayText: string;
    id: number;
}

export type QuickGraphModel = {
    objects: Array<QuickGraphObject>;
    tags: Array<QuickGraphTag>;
}

export type QuickGraphObjectSelect = QuickGraphObject & CheckListItemType;
export type QuickGraphTagSelect = QuickGraphTag & CheckListItemType;

export type ChartIdentifier = {
    id: string;
    index: number;
}

export interface GraphItemOptions extends Options {
    headerTitle?: string;
    id: string;
    index: number;
}

export interface IGraphSeriesOptions extends SeriesAbandsOptions {
    tagID?: number | undefined;
};

export type GraphSeriesOptionsType = SeriesOptionsType & {
    tagID: number;
}

export interface GraphOptions {
    isComparison?: boolean;
    isStacked?: boolean;
    optionItems: Array<GraphItemOptions>;
    listTextTagMapping?: IListTextTagMapping_V2 | undefined;
    synchronizeCharts?: boolean;
    synchronizeLegends?: boolean;
}

export interface ElementWithCharts extends Element {
    chartInstances: Map<ChartIdentifier, Highcharts.Chart>;
}

export interface IExportOptions {
    configurationID?: number;
    mimeType: ApplicationMimeTypesEnum;
    notificationKey: string;
    endDateTime?: Date;
    startDateTime?: Date;
    windowHeight?: number;
    windowWidth?: number;
}

export enum PageSizeOptionEnum {
    Letter = 0,
    Legal = 1,
    Ledger = 2
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export enum QuickTrendTypes {
    DefaultObject = 0,
    DefaultGroup = 1,
    SpecifiedObject = 2,
    SpecifiedGroup = 3
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendOptions2 {

    targetObjectType: eLynxObjectTypeEnum;

    // #region Report Options
    reportTextMemos: boolean;
    trendChartMemos: boolean;
    quickTrendType: QuickTrendTypes;
    reportPagesTableOfContents: boolean;
    reportTextTagAlarms: boolean;
    trendChartHideSecondaryAxes: boolean;
    trendChartTagAlarms: boolean;
    reportPagesTitlePage: boolean;
    // #endregion

    // #region Report Page Options
    pageHeightInches: number;
    pageWidthInches: number;
    // #endregion

    // #region Configuration Options
    configurationID?: number;
    // #endregion

    // #region UI only
    pageSize: PageSizeOptionEnum;
    // #endregion
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendExportModel {
    mimeType: ApplicationMimeTypesEnum;
    notificationKey: string;
    endDateTime?: Date | undefined;
    startDateTime?: Date | undefined;
    quickTrendOptions: IQuickTrendOptions2;
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendAlarmViewModel {
    acknowledgedByName?: string;
    acknowledgedTimestamp?: string;
    alarmDuration?: number;
    alarmEndingReason?: AlarmNotificationEndingReasonEnum;
    alarmStatus: boolean;
    eventDateTime: string;
    isAcknowledged: boolean;
    value: number;
    returnToNormal?: string;
    setPointEventType?: SetPointEventTypeEnum;
    setPointName: string;
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendMemoViewModel {
    dateTime?: string;
    hasUserInfo: boolean;
    memoType: MemoType;
    text: string;
    userInfo?: string;
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendTagViewModel {
    displayName: string;
    alarms: Array<IQuickTrendAlarmViewModel>;
    memos: Array<IQuickTrendMemoViewModel>;
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendObjectViewModel {
    errorMessage: string;
    id: number;
    objectDisplayName: string;
    objectMemos: Array<IQuickTrendMemoViewModel>;
    hasObjectMemos: boolean;
    hasTagMemos: boolean;
    hasTagAlarmsAnalog: boolean;
    hasTagAlarmsDiscrete: boolean;
    tagAlarmsAnalog: Array<IQuickTrendTagViewModel>;
    tagAlarmsDiscrete: Array<IQuickTrendTagViewModel>;
    tagMemos: Array<IQuickTrendTagViewModel>;
    chartOptions: GraphItemOptions;
}

// This is named QuickTrend instead of GraphReport due to all the shared
//  backend code with this name.
export interface IQuickTrendViewModel {
    listTextTagMapping: IListTextTagMapping_V2 | undefined;
    quickTrendObjects: Array<IQuickTrendObjectViewModel>;
}

export interface IFileStoreQueryParams {
    filestoreID: number;
}

export interface ITrendAdHocDataRequestModel {
    disableDeferredData: boolean;
    displayOptions?: ITrendingDisplayOptions;
    endTime?: Date;
    startTime?: Date;
    trendConfigurationModel: ITrendConfigurationDTO;
    windowHeight?: number;
    windowWidth?: number;
}